import React, { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useTranslation } from 'utils/i18n';
import { useUserType, VIEW_MODES } from 'utils/userTypeContext';
import { useToast } from '../ToastContainer';
import { MarketplaceFilters } from './MarketplaceFilters';
import { MarketplaceGrid } from './MarketplaceGrid';
import { ProviderGrid } from './ProviderGrid';
import { MarketplaceStepIndicator } from './MarketplaceStepIndicator';
import { TagFilter } from './TagFilter';
import { validateMarketplaceProduct } from '../ResourceDetailModal/utils/marketplaceMetadata';
import { getApiClient } from 'utils/apiClient';
import { Icons } from '../Icons';
import marketplaceTranslations from './translations';
import { logger } from 'utils/logger';
import '../ResourceDetailModal/styles/MarketplaceSelection.css';

/**
 * Step 1: Marketplace Selection
 * Full-width marketplace view for browsing and selecting products or providers
 */
export const MarketplaceSelection = ({
  products,
  loadingProducts,
  selectedProductId,
  onProductSelect,
  loading = false,
  selectedProduct = null,
  selectedPrice = null,
  showPriceStep = false,
  onNext = null,
  isAuthenticated = true,
  headerRightEl = null,
  isPublicPortal = true
}) => {
  // eslint-disable-next-line no-unused-vars
  const navigate = useNavigate();
  // eslint-disable-next-line no-unused-vars
  const { instance } = useMsal();
  const { language } = useTranslation();
  const { viewMode: userViewMode } = useUserType();
  const isCustomerMode = userViewMode === VIEW_MODES.NORMAL;
  const { showWarning } = useToast();
  const t = (key, defaultValue = '') => {
    const keys = key.split('.');
    let value = marketplaceTranslations[language] || marketplaceTranslations.en;
    for (const k of keys) {
      value = value?.[k];
    }
    return value || defaultValue || key;
  };
  const [searchParams, setSearchParams] = useSearchParams();
  const viewParam = searchParams.get('view') || 'products';
  const productParam = searchParams.get('product') || '';
  const providerParam = searchParams.get('provider') || '';

  const [viewMode, setViewMode] = useState(viewParam);
  // eslint-disable-next-line no-unused-vars
  const [openModalProductId, setOpenModalProductIdState] = useState(productParam || null);
  const [providers, setProviders] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [providerTags, setProviderTags] = useState([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [, setCostPools] = useState([]);
  const [loadingCostPools, setLoadingCostPools] = useState(false);
  const [hasValidPaymentMethod, setHasValidPaymentMethod] = useState(false);
  const loadingRef = useRef(false);
  const circuitBreakerOpenRef = useRef(false);
  const [filters, setFilters] = useState({
    vendorType: 'all',
    providerId: 'all',
    categoryId: 'all',
    searchQuery: '',
    selectedTags: [], // For tag filtering
    productType: 'all', // Implementation, Application, Partner
    releaseStatus: 'all' // Available, Coming Soon
  });

  const updateUrlParams = (updates) => {
    const next = new URLSearchParams(searchParams);
    if (updates.view !== undefined) next.set('view', updates.view);
    if (updates.product !== undefined) {
      if (updates.product) next.set('product', updates.product);
      else next.delete('product');
    }
    if (updates.provider !== undefined) {
      if (updates.provider) next.set('provider', updates.provider);
      else next.delete('provider');
    }
    setSearchParams(next, { replace: true });
  };

  // eslint-disable-next-line no-unused-vars
  const setOpenModalProductId = (id) => {
    setOpenModalProductIdState(id ?? null);
    updateUrlParams({ product: id ?? '' });
  };

  const setViewModeAndUrl = (mode) => {
    setViewMode(mode);
    if (mode === 'products') {
      updateUrlParams({ view: 'products', provider: '' });
      setSelectedProvider(null);
    } else {
      updateUrlParams({ view: 'providers', product: '' });
      setOpenModalProductIdState(null);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleOpenProvider = ({ providerId }) => {
    setViewMode('providers');
    setOpenModalProductIdState(null);
    updateUrlParams({ view: 'providers', product: '', provider: providerId });
  };

  const handleProviderSelect = (provider) => {
    setSelectedProvider(provider);
    updateUrlParams({ provider: provider?.id ?? '' });
  };

  useEffect(() => {
    const v = searchParams.get('view') || 'products';
    const p = searchParams.get('product') || '';
    setViewMode(v);
    setOpenModalProductIdState(p ? p : null);
  }, [searchParams]);

  useEffect(() => {
    if (viewMode !== 'providers' || !providerParam || providers.length === 0) return;
    const provider = providers.find(pr => pr.id === providerParam);
    if (provider) setSelectedProvider(provider);
  }, [viewMode, providerParam, providers]);

  // Load cost pools and check payment methods for customer mode users
  useEffect(() => {
    if (isCustomerMode && isAuthenticated) {
      checkPaymentMethods();
    } else {
      // Reset state if not customer mode or not authenticated
      setHasValidPaymentMethod(false);
      setCostPools([]);
      setLoadingCostPools(false);
      loadingRef.current = false;
      circuitBreakerOpenRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCustomerMode, isAuthenticated]); // Only run when mode or auth status changes

  // Load providers on mount (for both Products tab merge and Providers view)
  useEffect(() => {
    loadProviders();
  }, []);

  // Load provider products for all providers (for merged Products tab)
  const [providerProductsCache, setProviderProductsCache] = useState({});
  const [loadingProviderProducts, setLoadingProviderProducts] = useState(false);
  const providerIdsKey = providers.map(p => p.id).sort().join(',');
  useEffect(() => {
    if (providers.length === 0) return;
    let cancelled = false;
    const load = async () => {
      setLoadingProviderProducts(true);
      const apiClient = getApiClient();
      const cache = {};
      for (const provider of providers) {
        if (cancelled) return;
        try {
          const res = await apiClient.get(`/api/v1/marketplace/products/${provider.id}`);
          const raw = (res.data && res.data.products) || res.products;
          cache[provider.id] = Array.isArray(raw) ? raw : [];
        } catch (err) {
          logger.warn('MARKETPLACE', `Failed to load products for provider ${provider.id}`, { error: err.message });
          cache[provider.id] = [];
        }
      }
      if (!cancelled) setProviderProductsCache(cache);
      setLoadingProviderProducts(false);
    };
    load();
    return () => { cancelled = true; };
  }, [providerIdsKey]);

  // Load provider tags when view mode is 'providers'
  useEffect(() => {
    if (viewMode === 'providers') {
      loadProviderTags();
    }
  }, [viewMode]);

  const checkPaymentMethods = async () => {
    // Prevent duplicate calls
    if (loadingRef.current) {
      return;
    }
    
    // If circuit breaker is open, don't retry
    if (circuitBreakerOpenRef.current) {
      logger.warn('MARKETPLACE', 'Circuit breaker is open for /v1/cost-pools, skipping request');
      setHasValidPaymentMethod(false);
      setCostPools([]);
      setLoadingCostPools(false);
      return;
    }
    
    try {
      loadingRef.current = true;
      setLoadingCostPools(true);
      const apiClient = getApiClient();
      
      // Load only cost pools that have a valid payment method — backend filters on valid_payment_method=true
      const response = await apiClient.listCostPools({ requirePaymentMethod: true });
      
      // Handle different response structures
      let allCostPools = [];
      if (response && response.success && response.data) {
        allCostPools = Array.isArray(response.data) ? response.data : [];
      } else if (Array.isArray(response)) {
        allCostPools = response;
      } else if (response && Array.isArray(response.data)) {
        allCostPools = response.data;
      }
      
      setCostPools(allCostPools);
      
      // Backend already filtered to only pools with valid_payment_method=true,
      // so any returned pool is usable.
      setHasValidPaymentMethod(allCostPools.length > 0);
      circuitBreakerOpenRef.current = false; // Reset circuit breaker flag on success
    } catch (err) {
      // Check if this is a circuit breaker error
      const isCircuitBreakerError = err?.message?.includes('Circuit breaker open') || 
                                    err?.message?.includes('circuit breaker');
      
      if (isCircuitBreakerError) {
        logger.warn('MARKETPLACE', 'Circuit breaker is open for /v1/cost-pools', { error: err.message });
        circuitBreakerOpenRef.current = true;
      } else {
        logger.error('MARKETPLACE', 'Error checking payment methods', { error: err.message });
      }
      
      // On error, assume no valid payment method (show warning)
      setHasValidPaymentMethod(false);
      setCostPools([]);
    } finally {
      loadingRef.current = false;
      setLoadingCostPools(false);
    }
  };

  const loadProviders = async () => {
    try {
      setLoadingProviders(true);
      const apiClient = getApiClient();
      
      // Use new public marketplace API (no authentication required)
      const response = await apiClient.get('/api/v1/marketplace/providers');
      
      if (response.success && response.providers) {
        // Transform provider data to match expected format (include logo_url for product cards)
        const formattedProviders = response.providers.map(provider => ({
          id: provider.id,
          name: provider.name,
          description: provider.description,
          logo_url: provider.logo_url ?? null,
          metadata: {
            partner_profile: {
              description: provider.description,
              logo_url: provider.logo_url,
              marketplace_visible: true
            },
            tags: provider.tags || []
          },
          is_partner: provider.is_partner,
          is_besper: provider.is_besper
        }));
        setProviders(formattedProviders);
      }
    } catch (err) {
      logger.error('MARKETPLACE', 'Error loading providers', { error: err.message });
    } finally {
      setLoadingProviders(false);
    }
  };

  const loadProviderTags = async () => {
    try {
      setLoadingTags(true);
      const apiClient = getApiClient();
      
      // Use new public marketplace API (no authentication required)
      const providersResponse = await apiClient.get('/api/v1/marketplace/providers');
      
      if (!providersResponse.success || !providersResponse.providers) {
        setProviderTags([]);
        return;
      }
      
      const allTags = new Set();
      
      // Collect tags from all providers
      for (const provider of providersResponse.providers) {
        // Add provider-level tags
        if (provider.tags && Array.isArray(provider.tags)) {
          provider.tags.forEach(tag => {
            if (tag && typeof tag === 'string' && tag.trim()) {
              allTags.add(tag.trim());
            }
          });
        }
        
        // Also fetch product tags for each provider
        try {
          const productsResponse = await apiClient.get(`/api/v1/marketplace/products/${provider.id}`);
          if (productsResponse.success && productsResponse.products) {
            productsResponse.products.forEach(product => {
              if (product.tags && Array.isArray(product.tags)) {
                product.tags.forEach(tag => {
                  if (tag && typeof tag === 'string' && tag.trim()) {
                    allTags.add(tag.trim());
                  }
                });
              }
            });
          }
        } catch (err) {
          // Continue with other providers if one fails
          logger.warn('MARKETPLACE', `Failed to load products for provider ${provider.id}`, { error: err.message });
        }
      }
      
      setProviderTags(Array.from(allTags).sort());
    } catch (err) {
      logger.error('MARKETPLACE', 'Error loading provider tags', { error: err.message });
      setProviderTags([]);
    } finally {
      setLoadingTags(false);
    }
  };
  
  const handleFiltersChange = (newFilters) => {
    logger.debug('MARKETPLACE', 'Filters changed', { newFilters, previousFilters: filters });
    setFilters(prevFilters => {
      const updated = { ...prevFilters, ...newFilters };
      logger.debug('MARKETPLACE', 'Updated filters', { updated });
      return updated;
    });
  };
  
  // Handle product selection (auto-navigation handled in MarketplaceModule)
  const handleProductSelect = (product) => {
    // Check if user needs payment method and show notification
    if (isCustomerMode && isAuthenticated && !loadingCostPools && !hasValidPaymentMethod) {
      showWarning(
        t('paymentWarning.title', 'Payment Method Required'),
        t('paymentWarning.message', 'You need to add a valid payment method to your cost pool before you can purchase products. Please set up your payment profile first.'),
        { duration: 8000 }
      );
      return; // Don't proceed with product selection
    }
    
    if (onProductSelect) {
      onProductSelect(product);
    }
  };

  // Merged product list for Products tab: payment products + resale/partner products (normalized for filters and cards)
  const mergedProductsForProductsTab = useMemo(() => {
    const paymentIds = new Set((products || []).map(p => p.id));
    const normalized = (products || []).slice();
    for (const provider of providers) {
      const list = providerProductsCache[provider.id] || [];
      for (const p of list) {
        if (paymentIds.has(p.id)) continue;
        paymentIds.add(p.id);
        const providerLogoUrl = provider.logo_url ?? provider.metadata?.partner_profile?.logo_url ?? null;
        normalized.push({
          id: p.id,
          name: p.name || 'Unnamed Product',
          description: p.description || '',
          metadata: { ...(p.metadata || {}), provider_id: provider.id, provider: provider.id },
          marketplace: {
            providerId: provider.id,
            provider: { id: provider.id, displayName: provider.name, logo_url: providerLogoUrl },
            providerDisplayName: provider.name,
            providerLogoUrl,
            shortDescription: p.description || '',
            categoryId: (p.metadata && p.metadata.category) || 'other',
            tags: Array.isArray(p.tags) ? p.tags : [],
            vendorType: 'third_party'
          },
          ...p
        });
      }
    }
    return normalized;
  }, [products, providers, providerProductsCache]);
  
  // Validate products and show errors for Besper products with missing metadata
  // Skip validation for archived/inactive products (active: false means archived in Stripe)
  const productErrors = useMemo(() => {
    const errors = [];
    products.forEach(product => {
      // Skip archived products - they shouldn't be validated or shown
      if (product.active === false || product.rawProduct?.active === false) {
        return;
      }
      
      // Get raw product for validation (normalized products have rawProduct property)
      const rawProduct = product.rawProduct || product;
      const validation = validateMarketplaceProduct(rawProduct);
      if (!validation.isValid && (rawProduct.metadata?.vendor_type === 'besper' || !rawProduct.metadata?.vendor_type)) {
        errors.push({
          productId: product.id || rawProduct.id,
          productName: product.name || rawProduct.name || t('marketplaceCard.unnamedProduct', 'Unnamed Product'),
          errors: validation.errors
        });
      }
    });
    return errors;
  }, [products]);
  
  // If product is selected and we should show price step, don't render marketplace
  if (selectedProduct && showPriceStep) {
    return null; // PriceSelection will be rendered instead
  }
  
  const viewModeToggle = (
    <div style={{
      display: 'inline-flex',
      background: '#fafbfc',
      borderRadius: '4px',
      padding: '2px',
      border: '0.5px solid #e5e7eb',
      boxShadow: '0 0.5px 1px rgba(0, 0, 0, 0.04)',
      gap: '2px'
    }}>
      <button
        type="button"
        onClick={() => setViewModeAndUrl('products')}
        style={{
          padding: '6px 14px',
          borderRadius: '3px',
          border: 'none',
          background: viewMode === 'products' ? '#022d54' : 'transparent',
          color: viewMode === 'products' ? '#ffffff' : '#111827',
          cursor: 'pointer',
          fontSize: '0.8125rem',
          fontWeight: viewMode === 'products' ? '600' : '400',
          letterSpacing: '-0.01em',
          transition: 'all 0.15s ease',
          whiteSpace: 'nowrap',
          outline: 'none'
        }}
        onMouseEnter={(e) => {
          if (viewMode !== 'products') {
            e.target.style.background = '#f3f4f6';
            e.target.style.color = '#374151';
          }
        }}
        onMouseLeave={(e) => {
          if (viewMode !== 'products') {
            e.target.style.background = 'transparent';
            e.target.style.color = '#111827';
          }
        }}
      >
        {t('viewMode.products', 'Products')}
      </button>
      <button
        type="button"
        onClick={() => setViewModeAndUrl('providers')}
        style={{
          padding: '6px 14px',
          borderRadius: '3px',
          border: 'none',
          background: viewMode === 'providers' ? '#022d54' : 'transparent',
          color: viewMode === 'providers' ? '#ffffff' : '#111827',
          cursor: 'pointer',
          fontSize: '0.8125rem',
          fontWeight: viewMode === 'providers' ? '600' : '400',
          letterSpacing: '-0.01em',
          transition: 'all 0.15s ease',
          whiteSpace: 'nowrap',
          outline: 'none'
        }}
        onMouseEnter={(e) => {
          if (viewMode !== 'providers') {
            e.target.style.background = '#f3f4f6';
            e.target.style.color = '#374151';
          }
        }}
        onMouseLeave={(e) => {
          if (viewMode !== 'providers') {
            e.target.style.background = 'transparent';
            e.target.style.color = '#111827';
          }
        }}
      >
        {t('viewMode.providers', 'Providers')}
      </button>
    </div>
  );

  const stepsAndActions = (
    <div className="marketplace-steps-inline">
      <MarketplaceStepIndicator
        currentStep={1}
        selectedProduct={selectedProduct}
        selectedPrice={selectedPrice}
        showPriceStep={false}
      />
    </div>
  );

  const breadcrumbWithStepsContent = (
    <div className={`marketplace-breadcrumb-with-steps${headerRightEl ? ' marketplace-breadcrumb-with-steps--in-header' : ''}`}>
      {!headerRightEl && (
        <div className="marketplace-breadcrumb">
          <span>{t('breadcrumb.home', 'Home')}</span>
          <span className="breadcrumb-separator">›</span>
          <span>{t('breadcrumb.createResource', 'Create Resource')}</span>
          <span className="breadcrumb-separator">›</span>
          <span className="breadcrumb-current">{t('breadcrumb.marketplace', 'Marketplace')}</span>
        </div>
      )}
      {headerRightEl && <div className="marketplace-breadcrumb-with-steps__spacer" />}
      {stepsAndActions}
    </div>
  );

  return (
    <div className="marketplace-selection-step">
      {headerRightEl && createPortal(breadcrumbWithStepsContent, headerRightEl)}
      {!headerRightEl && breadcrumbWithStepsContent}
      
      {/* Error Banner */}
      {productErrors.length > 0 && (
        <div className="marketplace-error">
          <h4>{t('errors.productsMissingMetadata', '⚠️ Products with Missing Metadata')}</h4>
          <p>{t('errors.productsMissingMetadataDescription', 'The following products are missing required marketplace metadata:')}</p>
          <ul>
            {productErrors.map(({ productId, productName, errors }) => (
              <li key={productId}>
                <strong>{productName}</strong>: {errors.join(', ')}
              </li>
            ))}
          </ul>
          <p style={{ marginTop: '8px', fontSize: '0.8125rem' }}>
            {t('errors.updateStripeMetadata', 'Please update these products in Stripe with the required metadata fields.')}
          </p>
        </div>
      )}

      {/* Main Content */}
      <div className="marketplace-selection-layout">
        {viewMode === 'products' ? (
          <>
            {/* Filters Sidebar */}
            <aside className="marketplace-filters-sidebar">
              <MarketplaceFilters
                products={mergedProductsForProductsTab}
                filters={filters}
                onFiltersChange={handleFiltersChange}
                viewMode={viewMode}
                marketplaceProviders={providers}
              />
            </aside>
            
            {/* Product Grid Section */}
            <main className="marketplace-grid-container">
              <MarketplaceGrid
                products={mergedProductsForProductsTab}
                filters={filters}
                selectedProductId={selectedProductId}
                onProductSelect={handleProductSelect}
                loading={loading}
                loadingProducts={loadingProducts || loadingProviderProducts}
                isAuthenticated={isAuthenticated}
                isPublicPortal={isPublicPortal}
                viewModeToggle={viewModeToggle}
              />
            </main>
          </>
        ) : (
          <>
            {/* Filters Sidebar for Providers */}
            <aside className="marketplace-filters-sidebar">
              <div className="marketplace-filters">
                {/* Search Bar */}
                <div className="filter-group">
                  <label className="filter-label">{t('filters.search', 'Search')}</label>
                  <div style={{ position: 'relative' }}>
                    <Icons.Search 
                      size={16} 
                      style={{ 
                        position: 'absolute', 
                        left: '12px', 
                        top: '50%', 
                        transform: 'translateY(-50%)',
                        color: 'var(--text-secondary)'
                      }} 
                    />
                    <input
                      type="text"
                      placeholder={t('filters.search', 'Search providers...')}
                      value={filters.searchQuery || ''}
                      onChange={(e) => handleFiltersChange({ searchQuery: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '10px 12px 10px 36px',
                        border: '1px solid var(--border-light)',
                        borderRadius: '6px',
                        background: 'var(--bg-primary)',
                        color: 'var(--text-primary)',
                        fontSize: '0.875rem'
                      }}
                    />
                  </div>
                </div>

                {/* Tag Filter */}
                <TagFilter
                  tags={providerTags}
                  selectedTags={filters.selectedTags || []}
                  onTagsChange={(selectedTags) => handleFiltersChange({ selectedTags })}
                  loading={loadingTags}
                />
              </div>
            </aside>
            
            {/* Provider Grid Section */}
            <main className="marketplace-grid-container">
              <ProviderGrid
                providers={providers}
                loading={loadingProviders}
                selectedProvider={selectedProvider}
                onProviderSelect={handleProviderSelect}
                products={products}
                onProductSelect={handleProductSelect}
                filters={filters}
                viewModeToggle={viewModeToggle}
              />
            </main>
          </>
        )}
      </div>
    </div>
  );
};


